import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { SEOHead } from '../components/SEOHead';
import { 
  Plane, Clock, Shield, Star, Phone, MapPin, 
  CheckCircle, ArrowRight, Users, Car 
} from 'lucide-react';

const FLEET_IMG = 'https://customer-assets.emergentagent.com/job_f40a108f-f3a3-4735-ac15-18353f3eece6/artifacts/sih2jxml_image0.jpeg';
const ESCALADE_IMG = 'https://customer-assets.emergentagent.com/job_31dc5cc9-4948-412e-afd9-88900b02bc09/artifacts/g0b5gai9_IMG_7208.jpg';
const AIRPORT_IMG = 'https://images.unsplash.com/photo-1655827763440-7905302b75ff?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwxfHxEdWxsZXMlMjBhaXJwb3J0JTIwdGVybWluYWwlMjBsdXh1cnklMjBjYXJ8ZW58MHx8fHwxNzc1MTY4NjQxfDA&ixlib=rb-4.1.0&q=85';

const schema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "serviceType": "Airport Car Service",
  "provider": {
    "@type": "LocalBusiness",
    "name": "Capital Chauffeur Services",
    "telephone": "+1-202-684-0534",
    "email": "contact@capitalchauffeurservices.com",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Washington",
      "addressRegion": "DC",
      "addressCountry": "US"
    },
    "priceRange": "$$$",
    "image": FLEET_IMG
  },
  "areaServed": {
    "@type": "Place",
    "name": "Dulles International Airport (IAD)"
  },
  "description": "Premium Dulles Airport car service with professional chauffeurs and luxury Cadillac Escalade fleet."
};

export default function DullesAirportPage() {
  return (
    <div data-testid="dulles-airport-page" className="min-h-screen bg-[#050505]">
      <SEOHead
        title="Dulles Airport Car Service | IAD Limo & Chauffeur | Capital Chauffeur Services"
        description="Premium Dulles Airport (IAD) car service in Washington DC. Professional chauffeur, black Cadillac Escalade, flight tracking, meet & greet. Book now."
        canonical="/dulles-airport-car-service"
        schema={schema}
      />

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center">
        <div className="absolute inset-0">
          <img
            src={ESCALADE_IMG}
            alt="Black Cadillac Escalade at Dulles International Airport IAD"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/40" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32">
          <p className="text-[#D4AF37] text-sm tracking-[0.3em] uppercase mb-4 font-medium">Dulles International Airport</p>
          <h1 data-testid="dulles-h1" className="text-4xl sm:text-5xl lg:text-6xl font-serif text-white mb-6 leading-tight">
            Dulles Airport<br />Car Service
          </h1>
          <p className="text-[#A3A3A3] text-lg max-w-xl mb-8 leading-relaxed">
            Arrive or depart from Washington Dulles International Airport in a luxury black Cadillac Escalade with a professional chauffeur at the wheel.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to="/book">
              <Button className="bg-[#D4AF37] text-black hover:bg-[#B5952F] rounded-none px-8 py-6 text-sm font-medium tracking-wider uppercase">
                Book Your Ride
              </Button>
            </Link>
            <a href="tel:+12026840534">
              <Button className="border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black rounded-none px-8 py-6 text-sm font-medium tracking-wider uppercase bg-transparent inline-flex items-center gap-2">
                <Phone className="w-4 h-4" /> (202) 684-0534
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#D4AF37] text-sm tracking-[0.3em] uppercase mb-3">Our IAD Airport Services</p>
            <h2 className="text-3xl font-serif text-white mb-4">Comprehensive Dulles Airport Transportation</h2>
            <p className="text-[#A3A3A3] max-w-2xl mx-auto">
              Whether you are catching an international flight or arriving from overseas, our Dulles Airport car service covers every aspect of your ground transportation needs.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: Plane, title: 'Airport Pickup & Drop-Off', desc: 'Curbside pickup at Dulles arrivals or timely drop-off at your terminal. We monitor your flight status in real time so your chauffeur is ready when you land.' },
              { icon: Clock, title: 'Flight Tracking & Monitoring', desc: 'Our team tracks every incoming and outgoing flight at IAD. If your flight is delayed or arrives early, we adjust your pickup time automatically.' },
              { icon: Shield, title: 'Meet & Greet Service', desc: 'Your chauffeur can meet you inside the terminal with a name sign, assist with luggage, and escort you to your waiting Escalade at the curb.' },
              { icon: Users, title: 'Corporate Airport Transfers', desc: 'Dedicated corporate accounts for frequent flyers. We handle executive teams, VIP guests, and international delegations arriving at Dulles.' },
              { icon: Car, title: 'Luxury Fleet at IAD', desc: 'Travel in our premium black Cadillac Escalade ESV fleet. Spacious interiors accommodate up to six passengers with ample room for luggage.' },
              { icon: Star, title: 'Round-Trip & Hourly Options', desc: 'Book a one-way transfer or schedule a round trip. Hourly chauffeur service is also available for multi-stop business trips from Dulles.' },
            ].map((service, i) => (
              <div key={i} className="border border-white/10 p-8 hover:border-[#D4AF37]/30 transition-colors">
                <service.icon className="w-8 h-8 text-[#D4AF37] mb-4" />
                <h3 className="text-white font-serif text-lg mb-3">{service.title}</h3>
                <p className="text-[#A3A3A3] text-sm leading-relaxed">{service.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Dulles */}
      <section className="py-20 px-6 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <p className="text-[#D4AF37] text-sm tracking-[0.3em] uppercase mb-3">About Dulles Airport</p>
            <h2 className="text-3xl font-serif text-white mb-6">Washington Dulles International Airport (IAD)</h2>
            <div className="space-y-4 text-[#A3A3A3] leading-relaxed">
              <p>
                Washington Dulles International Airport, located in Dulles, Virginia, is one of the busiest airports in the Mid-Atlantic region. Serving over 24 million passengers annually, IAD is the primary hub for international flights in the Washington, DC metropolitan area.
              </p>
              <p>
                Dulles Airport sits approximately 26 miles west of downtown Washington, DC, in Loudoun County, Virginia. The drive from DC to Dulles typically takes 40 to 60 minutes depending on traffic along the Dulles Access Road and Dulles Toll Road. During peak commute hours, travel times can extend significantly.
              </p>
              <p>
                Capital Chauffeur Services provides reliable black car service from Dulles Airport to all major destinations in the DC metro area, including downtown Washington, Georgetown, Arlington, Tysons Corner, Reston, Fairfax, and beyond. Our professional chauffeurs know every route and can navigate traffic to get you to your destination efficiently.
              </p>
            </div>
          </div>
          <div className="aspect-[4/3] overflow-hidden">
            <img
              src={AIRPORT_IMG}
              alt="Luxury black car at Dulles International Airport terminal pickup area"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-serif text-white mb-4">Why Choose Capital Chauffeur for Dulles Airport</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              'Professional, licensed chauffeurs with extensive knowledge of Dulles Airport and surrounding areas',
              'Real-time flight tracking ensures your driver is always on time, whether your flight arrives early or late',
              'Premium black Cadillac Escalade ESV fleet with leather interiors, climate control, and complimentary water',
              'Competitive flat-rate pricing from Dulles to downtown DC, Arlington, Tysons Corner, and Northern Virginia',
              'Available 24/7 for early morning departures and late-night arrivals at Dulles International',
              'Corporate billing, recurring bookings, and dedicated account management for business travelers',
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-4 p-4">
                <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                <p className="text-[#A3A3A3] leading-relaxed">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Fleet Image */}
      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="aspect-[21/9] overflow-hidden">
            <img
              src={FLEET_IMG}
              alt="Capital Chauffeur Services black Escalade fleet for Dulles Airport car service"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-20 px-6 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-serif text-white mb-8 text-center">Popular Routes from Dulles Airport</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { route: 'Dulles to Downtown DC', time: '40-60 min' },
              { route: 'Dulles to Georgetown', time: '45-65 min' },
              { route: 'Dulles to Arlington, VA', time: '30-45 min' },
              { route: 'Dulles to Tysons Corner', time: '15-25 min' },
              { route: 'Dulles to Reston, VA', time: '10-20 min' },
              { route: 'Dulles to Bethesda, MD', time: '50-70 min' },
            ].map((item, i) => (
              <div key={i} className="flex items-center justify-between border border-white/10 p-5">
                <div className="flex items-center gap-3">
                  <MapPin className="w-4 h-4 text-[#D4AF37]" />
                  <span className="text-white text-sm">{item.route}</span>
                </div>
                <span className="text-[#D4AF37] text-xs">{item.time}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Internal Links */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-3xl font-serif text-white mb-4">Explore Our Other Services</h2>
          <p className="text-[#A3A3A3] mb-10 max-w-xl mx-auto">
            Capital Chauffeur Services provides premium transportation throughout the Washington, DC metro area and Northern Virginia.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            {[
              { label: 'Reagan Airport Car Service', path: '/reagan-airport-car-service' },
              { label: 'Corporate Chauffeur NoVA', path: '/corporate-chauffeur-northern-virginia' },
              { label: 'Black Car Service Arlington', path: '/black-car-service-arlington' },
              { label: 'Black Car Service Tysons', path: '/black-car-service-tysons' },
              { label: 'All Services', path: '/services' },
            ].map((link, i) => (
              <Link key={i} to={link.path}>
                <Button variant="outline" className="border-white/20 text-[#A3A3A3] hover:border-[#D4AF37] hover:text-[#D4AF37] rounded-none px-6 py-3 text-xs tracking-wider uppercase bg-transparent inline-flex items-center gap-2">
                  {link.label} <ArrowRight className="w-3 h-3" />
                </Button>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 bg-[#0A0A0A] border-t border-white/5">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-serif text-white mb-4">Book Your Dulles Airport Car Service</h2>
          <p className="text-[#A3A3A3] mb-8">
            Reserve your luxury airport transfer today. Professional chauffeurs, premium vehicles, and on-time service guaranteed.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/book">
              <Button className="bg-[#D4AF37] text-black hover:bg-[#B5952F] rounded-none px-10 py-6 text-sm font-medium tracking-wider uppercase">
                Book Now
              </Button>
            </Link>
            <a href="tel:+12026840534">
              <Button className="border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black rounded-none px-10 py-6 text-sm font-medium tracking-wider uppercase bg-transparent inline-flex items-center gap-2">
                <Phone className="w-4 h-4" /> Call (202) 684-0534
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
